import type { FC, PropsWithChildren } from "react";

import { colorsPalettes } from "@similarweb/styles";
import type { TextProps } from "@similarweb/ui-components/foundations";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { TruncatedTextWithTooltip } from "app/pages/sales-intelligence/common-components/TruncatedText";
import type { ContactSearchEntry } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";

import { StyledContactBaseInfoStack, StyledContactNameText } from "./styles";

const LONG_COUNTRY_NAME_LENGTH = 10;
const LONG_CONTACT_NAME_LENGTH = 25;

interface IProps {
    contact: ContactSearchEntry;
    isDisabledPrivateData?: boolean;
}

const ContactBaseInfo = ({ contact, isDisabledPrivateData, children }: PropsWithChildren<IProps>) => {
    const { name, title, country } = contact;

    const isLongCountryName = country.length > LONG_COUNTRY_NAME_LENGTH;
    const CountryNameComponent: FC<TextProps> = isLongCountryName ? TruncatedTextWithTooltip : Text;
    const countryNameMinWidth: string = isLongCountryName ? "65px" : "unset";
    const contactName = isDisabledPrivateData ? name.slice(0, LONG_CONTACT_NAME_LENGTH) : name;

    return (
        <StyledContactBaseInfoStack
            gap="10px"
            data-automation="top-companies.contact-details"
        >
            <Stack gap="2px">
                <StyledContactNameText
                    fontVariant="body2"
                    color={colorsPalettes.carbon[400]}
                    isHidden={isDisabledPrivateData}
                >
                    {contactName}
                </StyledContactNameText>
                <Flex gap="8px">
                    <TruncatedTextWithTooltip
                        fontVariant="listItem3"
                        color={colorsPalettes.carbon[250]}
                    >
                        {title}
                    </TruncatedTextWithTooltip>
                    <Text
                        fontVariant="listItem3"
                        color={colorsPalettes.carbon[250]}
                    >
                        {"•"}
                    </Text>
                    <CountryNameComponent
                        fontVariant="listItem3"
                        color={colorsPalettes.carbon[250]}
                        minWidth={countryNameMinWidth}
                    >
                        {country}
                    </CountryNameComponent>
                </Flex>
            </Stack>
            {children}
        </StyledContactBaseInfoStack>
    );
};

export default ContactBaseInfo;
